<template>
  <AppMainDrawer
    :use-wide-display-breakpoint="route.name !== 'job-profile-templates'"
    :center-content-on-close="route.name === 'job-profile-templates'"
  >
    <template #drawer>
      <DrawerStyled v-if="pageHasFilters[$route.name]">
        <template #drawer-content>
          <ExpandableFilters />
        </template>
      </DrawerStyled>
    </template>

    <template #content>
      <div class="w-100 job-profiles-library">
        <LibrariesTopBar
          v-model="filterDrawerStore.open"
          :totals="topBarTotals"
        >
          <template
            v-if="route.name === 'federal-flsa-dashboard'"
            #page-title="{ libraryLoading: titleLoading, pageDescription }"
          >
            <TopBarForDrawerTitle
              :is-loading="titleLoading"
              page-title="Federal FLSA Dashboard"
            >
              <template #title-trailing>
                <DpplSwitch
                  left="Draft"
                  right="HRIS Linked"
                  :value="flsaViewMode === 'draft'"
                  :width="220"
                  new-version
                  class="flsa-view-toggle"
                  @change="handleFlsaViewModeChange"
                />
              </template>
              <template #page-description>
                <div class="flsa-page-info">
                  <DpplSafeHtml :html="pageDescription" />
                  <p
                    v-if="!titleLoading"
                    class="text-body-2 text-dark flsa-profile-count mt-1"
                  >
                    {{ numberWithCommas(flsaProfileCount) }} job profiles
                  </p>
                </div>
              </template>
            </TopBarForDrawerTitle>
          </template>
          <template #summaries="{ libraryLoading, totals }">
            <div
              v-if="route.name === 'employee-acknowledgements'"
              class="employee-ack-summaries-slot"
            >
              <TeamSummary
                :totals="employeeAcknowledgementTeamSummaryTotals(totals)"
                :loading="libraryLoading"
                :show-datapeople-anywhere-line="false"
                inactive-label="Inactive Employees"
              />
            </div>
            <DocumentLibrarySummary
              v-else-if="route.name !== 'federal-flsa-dashboard' && route.name !== 'approvals-library'"
              :is-loading="libraryLoading"
              :totals="totals"
              :document-type="route.name"
            />
          </template>
          <template #next-to-filters>
            <EmployeeAcknowledgementSearch v-if="route.name === 'employee-acknowledgements'" />
            <JobProfileHrisSearch v-if="route.name === 'hris-job-profiles'" />
            <JobProfileDraftSearch v-if="route.name === 'draft-job-profiles'" />
            <FlsaSurveySearch v-if="route.name === 'federal-flsa-dashboard'" />
            <ApprovalsSearch v-if="route.name === 'approvals-library'" />
            <div
              v-if="showCompareActions"
              class="ml-auto"
            >
              <v-btn
                color="brandPrimary"
                variant="flat"
                class="compare-selected-btn text-button-2"
                :disabled="disableCompare"
                @click="openCompare"
              >
                Compare Selected ({{ numOfDocumentsToCompare }})
              </v-btn>
              <v-btn
                variant="text"
                color="brandPrimary"
                class="text-button-3"
                :disabled="disableCompare"
                @click="clearCompare"
              >
                Clear
              </v-btn>
            </div>
          </template>
          <template
            v-if="route.name === 'employee-acknowledgements'"
            #above-filters="{ libraryLoading, totals }"
          >
            <EmployeeAcknowledgementSummaryCards
              :is-loading="libraryLoading"
              :totals="totals"
              class="w-100"
            />
          </template>
          <template
            v-else-if="route.name === 'federal-flsa-dashboard'"
            #above-filters="{ libraryLoading, totals }"
          >
            <FlsaSurveySummaryCards
              :is-loading="libraryLoading"
              :totals="totals"
              class="w-100"
            />
          </template>
          <template
            v-else-if="route.name === 'approvals-library'"
            #above-filters="{ libraryLoading, totals }"
          >
            <ApprovalsSummaryCards
              :is-loading="libraryLoading"
              :totals="totals"
              class="w-100"
            />
          </template>
          <template
            v-if="route.name === 'employee-acknowledgements'"
            #actions-trailing="{ libraryLoading }"
          >
            <EmployeeAcknowledgementActions
              :library-loading="libraryLoading"
              :selected-count="selectedEmployeeAcknowledgementIds.size"
            />
          </template>
          <template
            v-else-if="route.name === 'federal-flsa-dashboard'"
            #actions-trailing="{ libraryLoading }"
          >
            <FlsaSurveyActions :library-loading="libraryLoading" />
          </template>
          <template
            v-else-if="route.name === 'approvals-library'"
            #actions-trailing="{ libraryLoading }"
          >
            <ApprovalsActions :library-loading="libraryLoading" />
          </template>
        </LibrariesTopBar>

        <div>
          <router-view
            v-slot="{ Component }"
            @track-page-load="emit('track-page-load', $event)"
          >
            <component :is="Component" />
          </router-view>
        </div>
      </div>
    </template>
  </AppMainDrawer>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import AppMainDrawer from '@/components/shared/AppMainDrawer.vue';
import DrawerStyled from '@/components/shared/DrawerStyled.vue';
import LibrariesTopBar from '@/components/library/LibrariesTopBar.vue';
import DocumentLibrarySummary from '@/components/library/DocumentLibrarySummary.vue';
import TeamSummary from '@/components/settings/team/TeamSummary.vue';
import EmployeeAcknowledgementActions from
  '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementActions.vue';
import EmployeeAcknowledgementSearch from
  '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementSearch.vue';
import EmployeeAcknowledgementSummaryCards from
  '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementSummaryCards.vue';
import ExpandableFilters from '@/components/filter/ExpandableFilters.vue';
import { useLibrary } from '@/stores/library/library';
import { useAppBase } from '@/stores/app/base';
import { useRequisitionLibrary } from '@/stores/requisitions/requisitionLibrary';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { useJobProfileDrafts } from '@/stores/jobProfiles/jobProfileDrafts';
import { useFilters } from '@/stores/filters/filters';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import JobProfileHrisSearch from '@/components/jobProfiles/JobProfileHrisSearch.vue';
import JobProfileDraftSearch from '@/components/jobProfiles/JobProfileDraftSearch.vue';
import TopBarForDrawerTitle from '@/components/shared/topbar/TopBarForDrawerTitle.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import DpplSwitch from '@/components/datapeople/DpplSwitch.vue';
import { numberWithCommas } from '@/helpers/formatters';
import FlsaSurveySearch from '@/components/library/flsaSurveys/FlsaSurveySearch.vue';
import FlsaSurveySummaryCards from '@/components/library/flsaSurveys/FlsaSurveySummaryCards.vue';
import FlsaSurveyActions from '@/components/library/flsaSurveys/FlsaSurveyActions.vue';
import { useFlsaSurveysStore } from '@/stores/jobProfiles/flsaSurveys';
import { useTracking } from '@/stores/tracking/tracking';
import {
  JP_PAGE_DRAFT_LIBRARY,
  JP_PAGE_HRIS_LIBRARY,
} from '@/helpers/jobProfileLibraryTracking';
import ApprovalsSearch from '@/components/library/approvals/ApprovalsSearch.vue';
import ApprovalsSummaryCards from '@/components/library/approvals/ApprovalsSummaryCards.vue';
import ApprovalsActions from '@/components/library/approvals/ApprovalsActions.vue';
import { useApprovalsDashboardStore } from '@/stores/jobProfiles/approvalsDashboard';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const filterDrawerStore = useFilterDrawer();

const { pageHasFilters } = storeToRefs(useAppBase());

const { total } = storeToRefs(useRequisitionLibrary());

const {
  totals: employeeAcknowledgementTotals,
  selectedEmployeeAcknowledgementIds,
} = storeToRefs(useEmployeeAcknowledgementsStore());

const flsaSurveysStore = useFlsaSurveysStore();
const {
  totals: flsaSurveyTotals,
  profileCount: flsaProfileCount,
  viewMode: flsaViewMode,
} = storeToRefs(flsaSurveysStore);
const { setViewMode: setFlsaViewMode } = flsaSurveysStore;

const {
  totals: approvalsDashboardTotals,
} = storeToRefs(useApprovalsDashboardStore());

const { selectedProfiles: selectedHrisProfiles } = storeToRefs(useJobProfiles());
const { resetSelectedProfiles: resetSelectedHrisProfiles } = useJobProfiles();
const { selectedProfiles: selectedDraftProfiles } = storeToRefs(useJobProfileDrafts());
const { resetSelectedProfiles: resetSelectedDraftProfiles } = useJobProfileDrafts();

const { init } = useLibrary();

const { clearPersistentFilters, sendFilterRequest } = useFilters();

const { setSelectedSubMenuOption } = useSubMenu();

const topBarTotals = computed(() => {
  if (route.name === 'employee-acknowledgements') {
    return employeeAcknowledgementTotals.value;
  }

  if (route.name === 'federal-flsa-dashboard') {
    return flsaSurveyTotals.value;
  }

  if (route.name === 'approvals-library') {
    return approvalsDashboardTotals.value;
  }

  return { total_page_requisitions: total.value };
});

const showCompareActions = computed(() => (
  route.name === 'hris-job-profiles' || route.name === 'draft-job-profiles'
));

const isDraftCompareRoute = computed(() => route.name === 'draft-job-profiles');

const activeCompareSelection = computed(() => (
  isDraftCompareRoute.value ? selectedDraftProfiles.value : selectedHrisProfiles.value
));

const numOfDocumentsToCompare = computed(() => activeCompareSelection.value.size);

const disableCompare = computed(() => {
  if (!showCompareActions.value) return true;
  return Boolean(!activeCompareSelection.value.size);
});

const openCompare = () => {
  if (!activeCompareSelection.value.size) return;
  const convertSetToArray = Array.from(activeCompareSelection.value);

  useTracking().jpLibClickedCompare({
    page: isDraftCompareRoute.value ? JP_PAGE_DRAFT_LIBRARY : JP_PAGE_HRIS_LIBRARY,
    count: convertSetToArray.length,
  });

  router.push({
    name: 'job-architecture-compare',
    query: {
      ...(isDraftCompareRoute.value
        ? { draftProfiles: convertSetToArray.join(',') }
        : { profiles: convertSetToArray.join(',') }),
      from: route.name,
    },
  });
};

const clearCompare = () => {
  if (!activeCompareSelection.value.size) return;

  useTracking().jpLibClickedClearSelection({
    page: isDraftCompareRoute.value ? JP_PAGE_DRAFT_LIBRARY : JP_PAGE_HRIS_LIBRARY,
  });

  if (isDraftCompareRoute.value) {
    resetSelectedDraftProfiles();
  } else {
    resetSelectedHrisProfiles();
  }
};

const handleFlsaViewModeChange = async (isDraft) => {
  const mode = isDraft ? 'draft' : 'hris_linked';

  useTracking().flsaLibToggledViewMode({ mode: isDraft ? 'Draft' : 'HRIS' });

  setFlsaViewMode(mode);

  await router.push({
    query: {
      ...route.query,
      view: isDraft ? 'draft' : 'hris',
    },
  });

  sendFilterRequest({ router, pageNumber: 1 });
};

const employeeAcknowledgementTeamSummaryTotals = ({
  active = 0,
  inactive = 0,
} = {}) => ({
  totalActive: active,
  totalInactive: inactive,
});

onMounted(async () => {
  await init({ router });
});

onBeforeUnmount(() => {
  clearPersistentFilters();
  setSelectedSubMenuOption(null);
});
</script>

<style lang="less" scoped>
.job-profiles-library {
  @media screen and (min-width: 1800px) {
    max-width: 1400px;
  }
}

.employee-ack-summaries-slot {
  :deep(.user-status-count) {
    margin-right: 0;
    text-align: right;
  }
}
.compare-selected-btn {
  margin-left: auto;
  height: 40px !important;
  min-height: 40px !important;
  padding: 0 12px;
  border-radius: 8px !important;
}

.flsa-page-info {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

:deep(.dppl-switch.flsa-view-toggle.new-version) {
  height: 36px;
  border-radius: 36px;

  .button-background-container {
    padding: 4px;
  }

  .p2.button-background {
    height: 30px;
    font-size: 13px;
  }
}

:deep(.dppl-switch.flsa-view-toggle.new-version .p2.button-background.selected) {
  background: @brand-primary !important;
  color: @white !important;
}

.flsa-profile-count {
  white-space: nowrap;
  margin: 0;
  text-align: right;
}
</style>
